import React, { Dispatch, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../generic-components/Icon';
import { addLogoIcon } from '../../../../assets/svg';
import { MAX_FILE_SIZE, TEXTS } from '../consts/consts';
import Button from '../../../../generic-components/Button';
import Spinner, { SpinnerColor } from '../../../../generic-components/Spinner';
import { ReactComponent as TrashBin } from '../../../../assets/icons/svg/shared/ic-shared-trash.svg';

interface IProps {
  customLogo: File | null;
  setCustomLogo: Dispatch<File | null>;
  isLoading?: boolean;
  isAreaDisabled?: boolean;
  handleOnClick?: () => void;
}

const DragAndDropArea = ({ customLogo, setCustomLogo, isLoading, isAreaDisabled, handleOnClick }: IProps) => {
  const { t } = useTranslation();
  const hiddenFileInput = useRef(null);
  const [isNativeHover, setIsNativeHover] = useState<boolean>();
  const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];

  const validateFileFormat = (fileName: string) => {
    if (fileName) {
      return ACCEPTED_FILE_TYPES.includes(fileName);
    }
    return false;
  };

  const validateFileSize = (size: number) => size <= MAX_FILE_SIZE;
  const handleClick = () => {
    if (!customLogo) {
      if (hiddenFileInput.current) {
        (hiddenFileInput.current as HTMLInputElement).click();
      }
    } else {
      setCustomLogo(customLogo);
      if (handleOnClick) handleOnClick();
    }
  };
  const handleFileInput = (file: any) => {
    if (!file) return;
    if (validateFileFormat(file.type) && validateFileSize(file.size)) {
      setCustomLogo(file);
    } else console.error('Check file size or format');
    // TODO Ilia - Check error message with Robi
  };
  const dropHandler = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.items) {
      Array.from(event.dataTransfer.items).forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && validateFileFormat(file.type) && validateFileSize(file.size)) {
            setCustomLogo(file);
          } else console.error('Check file size or format');
          // TODO Ilia - Check error message with Robi
        }
      });
    }
  };

  const dragOverHandler = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <div
      role='button'
      tabIndex={0}
      onDragEnterCapture={() => setIsNativeHover(true)}
      onDragLeaveCapture={() => setIsNativeHover(false)}
      onClick={handleClick}
      onDragOver={dragOverHandler}
      onKeyDown={() => {}}
      onDrop={(event) => dropHandler(event)}
      className={`w-[532px] h-[215px] border-[2px] border-bw03 flex justify-center items-center overflow-hidden mt-[12px] rounded-lg ${customLogo !== null ? (isAreaDisabled ? 'border-bw03 bg-bw02' : 'border-action03 border-solid cursor-default') : 'cursor-pointer hover:bg-bw02 border-dashed'} ${isNativeHover ? 'bg-bw02' : ''}`}
    >
      {customLogo !== null ? (
        isLoading ? (
          <Spinner height='2rem' spinnerColor={SpinnerColor.Blue} width='2rem' />
        ) : (
          <>
            <Button
              onClick={() => setCustomLogo(null)}
              className='absolute w-[103px] h-[32px] mt-[-171px] mr-[-417px] shadow-lg bg-bw00 border border-bw03 rounded-[100px] flex items-center justify-center text-bw07 text-sm font-metric gap-1 hover:bg-bw02 hover:shadow-none'
              childElement={<TrashBin className='w-[18px] h-[18px] text bw07' />}
              title='Remove'
              id='removeImageButton'
            />
            <img className='w-full h-full object-contain' src={URL.createObjectURL(customLogo)} alt='customLogo' />
          </>
        )
      ) : (
        <div className='w-full h-full flex flex-col items-center justify-center'>
          <input
            id='handleFileInput'
            ref={hiddenFileInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (event.target.files) {
                handleFileInput(event?.target?.files[0]);
              }
            }}
            type='file'
            accept={ACCEPTED_FILE_TYPES.join(',')}
            hidden
          />
          <Icon className='w-[58px] h-[58px] mb-[14px]' src={addLogoIcon} />
          <span className='font-metric text-bw07 text-base'>
            {t(TEXTS.STEP_LOGO.DRAG_N_DROP.TEXT_1)}
            <span className='font-metricMedium underline'>{t(TEXTS.STEP_LOGO.DRAG_N_DROP.TEXT_2)}</span>
          </span>
          <span className='font-metric text-sm text-bw06'>{t(TEXTS.STEP_LOGO.DRAG_N_DROP.TEXT_3)}</span>
          <span className='font-metric text-sm text-bw06 mt-[24px]'>{t(TEXTS.STEP_LOGO.DRAG_N_DROP.TEXT_4)}</span>
        </div>
      )}
    </div>
  );
};

export default DragAndDropArea;
